import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { min } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import { pluralizeToString } from 'lib/string/pluralize'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import React, { useCallback, useContext, useMemo, useRef } from 'react'
import { getDurationOptions } from 'selectors/offerPage/offerPageSelectors'
import { OfferDurationOption } from '../DurationTabs/OfferDurationTabs'
import OfferDurationOptions from './OfferDurationOptions'
import OfferDurationSelectBox from './OfferDurationSelectBox'

interface Props {
  open: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onSelect?: () => void;
  onClose?: () => void;
  durationOptions: Array<OfferDurationOption>;
  className?: string;
  onDurationSelect?: () => void;
}

function OfferDurationSelect(props: Props) {
  const { open, durationOptions, onClick, onSelect = noop, onClose = noop, className, onDurationSelect } = props
  const triggerRef = useRef<HTMLDivElement>(null)

  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const isMobile = useScreenSizeOnly('mobile')

  const onApplyClose = useCallback(() => {
    dispatch({ type: OfferPageActions.closeFilter })
    trackEvent('DURATION_FILTER_CLOSED')
    onClose()
  }, [dispatch, trackEvent, onClose])

  const onContinue = useCallback(() => {
    dispatch({ type: OfferPageActions.filterContinue })
    trackEvent('DURATION_FILTER_CLOSED')
    onSelect()
  }, [dispatch, trackEvent, onSelect])

  const minimumDuration = useMemo(() => min(durationOptions, option => option.duration)?.duration ?? 1, [durationOptions])

  const handleApply = useCallback(() => {
    onContinue()
    onDurationSelect?.()
  }, [onContinue, onDurationSelect])

  return <div className={className}>
    <OfferDurationSelectBox
      ref={triggerRef}
      onClick={onClick}
    />
    <DropdownList
      size="M"
      open={open && !isMobile}
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      onClose={onApplyClose}
      title="How long do you want to stay?"
      subtitle={minimumDuration > 1 ? `Minimum stay for this offer is ${pluralizeToString('night', minimumDuration)}` : undefined}
      isPrimaryActionHidden={!onDurationSelect}
      primaryActionProps={{
        children: 'Apply',
        onClick: handleApply,
      }}
    >
      <OfferDurationOptions onSelect={onDurationSelect} />
    </DropdownList>
  </div>
}

export default withOfferPageAndReduxState((state, appState) => ({
  open: state.activeFilter === 'duration',
  durationOptions: getDurationOptions(state, appState),
}), OfferDurationSelect)
